/**
 * INSTITUIÇÃO E VÍNCULO — a forma, e de onde ela veio.
 *
 * Dezenove pontos do T2 pedem isto, e o mais importante deles manda COPIAR:
 *
 *   T2.65 "eu acho que o melhor modelo é o que a gente resolveu adotar no— eu vou ter que dar uma
 *          olhada exatamente qual foi o projeto (...) foi ou o XequeMath ou o EducaHubPlay que tem
 *          segmentado um recurso ali, de você vincular a escola, a instituição de ensino"
 *
 * ── QUAL DOS DOIS, E A RESPOSTA SAIU DE MEDIÇÃO ────────────────────────────────────────────────
 *
 * Isso era a dúvida `D1`, e ela travava os dezenove. O operador deu autonomia em 2026-09-12 para
 * resolvê-la, e ela não precisou de julgamento: precisou de alguém ir olhar.
 *
 *   `educahubplay`  tem `app/instituicao/page.tsx`, `lib/instituicao/{modelo,api}.ts`,
 *                   `modelo.test.ts`, e regras de `instituicoes/`, `vinculos/` e `turmas/` no
 *                   `firestore.rules`. É o recurso inteiro.
 *   `xequemath`     tem `app/candidatura/` e `app/admin/candidaturas/` — candidatura de EDUCADOR
 *                   aprovada por admin. É moderação, e não é vínculo com instituição.
 *
 * Não é escolha: é o único dos dois que tem a coisa. E há uma confirmação que fecha a questão —
 * o cabeçalho do `modelo.ts` de lá cita o desenho **na letra do operador, em 2026-09-09**, e
 * aquela fala é quase a mesma que ele deu aqui em **2026-09-10**:
 *
 *   lá  "só quem no cadastro se colocou como educador pode solicitar"
 *   cá  T2.66 "se você se cadastra como educador, você pode cadastrar uma instituição"
 *
 *   lá  "uma vez que um educador peça a criação e a gente provisione"
 *   cá  T2.74 "vai cair numa solicitação para que a equipe que gere o HQ-Lab verifique"
 *
 *   lá  "ele passa a moderar a vinculação de outros educadores — que ganham o MESMO poder"
 *   cá  T2.75 "todas as solicitações vão cair na mão desse moderador E DE OUTROS educadores"
 *
 * Ele contou a mesma história duas vezes, para dois produtos, com um dia de intervalo. Copiar o
 * modelo é literalmente o que o T2.65 pede.
 *
 * ── O QUE EU **NÃO** COPIEI, E É DECISÃO DELE ─────────────────────────────────────────────────
 *
 * O EducaHubPlay tem `Turma`. Aqui não tem, e não é esquecimento: `T2.80` pede turmas com um caso
 * concreto — três professores, três turmas, cada um querendo só a sua — e `T2.81`, **no bloco
 * seguinte**, adia: *"isso é uma coisa interessante, eu concordo, mas vamos deixar isso para
 * casos futuros. Se já puder botar todo mundo de uma instituição com um bocado de coisa de todas
 * as séries, todas as turmas juntas JÁ AJUDA."*
 *
 * Vinte e sete segundos entre as duas falas. Portar a turma junto seria trazer de volta o que ele
 * tirou de propósito.
 *
 * ── O QUE ESTE ARQUIVO NÃO FAZ ────────────────────────────────────────────────────────────────
 *
 * Não autoriza nada. Ele descreve a FORMA. Quem autoriza é `firestore.rules`, e a razão é a mesma
 * que a biblioteca já pagou: o cliente pode mentir sobre qualquer campo que ele mesmo grava.
 */

/** Papel de uma pessoa DENTRO de uma instituição. Diferente do tipo declarado no cadastro. */
export const PAPEIS_INSTITUICAO = ["educador", "aluno"] as const;
export type PapelInstituicao = (typeof PAPEIS_INSTITUICAO)[number];

/**
 * Situação de um vínculo.
 *
 * Vínculo nasce `pendente` SEMPRE — inclusive o de educador. `T2.78`: *"cai numa caixa de
 * solicitação desse moderador, ISSO PRECISA SER COMPOSTO COMO UM PEDIDO."* Deixar alguém entrar
 * direto tornaria a moderação decorativa.
 */
export const SITUACOES_VINCULO = ["pendente", "ativo", "recusado", "removido"] as const;
export type SituacaoVinculo = (typeof SITUACOES_VINCULO)[number];

/**
 * Situação da instituição. O provisionamento é da equipe, fora da plataforma.
 *
 * E o gesto da equipe são DOIS documentos, não um: `docs/provisionar-escola.md` diz quais, e por que o
 * segundo é o que ninguém lembra. Medido em 28/09/2026 contra o Firestore local — ativar a escola sem
 * criar o vínculo de educador ativo de quem a pediu deixa a escola **ativa e sem moderador**: os
 * estudantes pedem para entrar e os pedidos não caem na mão de ninguém, porque a fila é montada a
 * partir dos vínculos de quem `podeModerar`.
 *
 * `T2.74`: *"aquilo vai cair numa solicitação para que a equipe que gere o HQ-Lab verifique se
 * aquela instituição de fato existe e se você de fato está apto a ser um moderador dela."*
 */
export const SITUACOES_INSTITUICAO = ["solicitada", "ativa", "recusada", "suspensa"] as const;
export type SituacaoInstituicao = (typeof SITUACOES_INSTITUICAO)[number];

/**
 * O tipo de conta, declarado no cadastro.
 *
 * `T2.68`: *"na parte de criar conta vai ter que ter uma questão de tipo estudante ou professor e
 * tal, moderador."* `T2.72` fecha pela obrigatoriedade: *"devia colocar como obrigatório isso (...)
 * no mínimo se tiver estudante ou é [professor] para poder abrir esse tipo de opção."*
 *
 * São DOIS e não três: "moderador" não é tipo de conta — é o que um educador se torna dentro de
 * uma instituição, e isso é o `papel` do vínculo. Confundir os dois criaria uma pessoa que é
 * moderadora de nada.
 */
export const TIPOS_DE_CONTA = ["estudante", "educador"] as const;
export type TipoDeConta = (typeof TIPOS_DE_CONTA)[number];

export const ROTULO_TIPO_DE_CONTA: Record<TipoDeConta, string> = {
  estudante: "Estudante",
  educador: "Educador — professor, diretor ou funcionário da escola",
};

export interface Instituicao {
  id: string;
  nome: string;
  /** Sigla ou apelido curto. */
  apelido?: string;
  cidade?: string;
  uf?: string;
  situacao: SituacaoInstituicao;
  /** Quem pediu. Vira o primeiro moderador quando a instituição é ativada. */
  solicitanteUid: string;
  solicitanteEmail?: string;
  /** Por que foi recusada, quando for. Registrar a razão é o mínimo devido a quem pediu. */
  motivoRecusa?: string;
  criadaEm: string;
  ativadaEm?: string;
  /** Admin geral que ativou, recusou ou suspendeu (instituicoes-ensino, caso A). */
  ativadaPor?: string;
  /** Nível e rede, vindos da base do INEP. */
  nivel?: string;
  rede?: string;
}

export interface Vinculo {
  /** `${instituicaoId}_${uid}` — um vínculo por pessoa por instituição. */
  id: string;
  instituicaoId: string;
  uid: string;
  nome?: string;
  email?: string;
  papel: PapelInstituicao;
  situacao: SituacaoVinculo;
  /** Quem aprovou ou recusou. Sem isso, moderação não tem responsável. */
  moderadoPor?: string;
  moderadoEm?: string;
  criadoEm: string;
  /** Hora da recusa, em ms, conferida pelo banco — é dela que se contam os 7 dias do caso F. */
  recusadoEmMs?: number;
  /** Desde quando o pedido refeito aparece na fila: a recusa + 7 dias (o banco confere a conta). */
  visivelEmMs?: number;
  /** O nome oficial, guardado no pedido: o estudante pode pedir antes de a instituição existir no banco. */
  instituicaoNome?: string;
  /** Pedido (vai à fila) ou convite (entra já aprovado, caso E). */
  origem?: "pedido" | "convite";
  /** O código do convite usado, quando a origem é convite. */
  convite?: string;
}

/* ─────────────────────────── regras de forma ─────────────────────────── */

/**
 * Só quem se declarou educador no cadastro pode pedir instituição.
 *
 * `T2.69`: *"quem precisa dar o primeiro, a primeira introdução para criar essa biblioteca da
 * escola TERIA QUE SER UM PROFESSOR."*
 */
export function podeSolicitarInstituicao(tipo: TipoDeConta | undefined): boolean {
  return tipo === "educador";
}

/**
 * Quem modera vínculos: educador **ativo** da instituição.
 *
 * `T2.75` é explícito sobre não haver hierarquia: *"todas as solicitações daquela instituição vão
 * cair na mão desse moderador **e de outros, por exemplo, outros educadores**"*. Então o primeiro
 * não é dono — é só o primeiro. Isso evita o produto ficar refém de uma pessoa que sai da escola,
 * e é a mesma decisão que o EducaHubPlay tomou.
 */
export function podeModerar(v: Vinculo | undefined): boolean {
  return v?.papel === "educador" && v.situacao === "ativo";
}

/**
 * Quem publica NA instituição: só educador ativo.
 *
 * `T2.63`: os quadrinhos vinculados *"só vão ser visíveis para um profissional, um educador
 * daquela, educador ou aluno daquela instituição"* — ler é de qualquer vínculo ativo. Quem PÕE
 * material lá é o educador, pela mesma razão que ele modera: é a conta que a escola respondeu por.
 */
export function podePublicarNaInstituicao(v: Vinculo | undefined): boolean {
  return podeModerar(v);
}

/** Quem consome o material da instituição: qualquer vínculo ativo, educador ou aluno. */
export function podeVerMaterialDaInstituicao(v: Vinculo | undefined): boolean {
  return v?.situacao === "ativo";
}

/** O id do vínculo é DERIVADO, nunca sorteado — assim não existem dois para a mesma pessoa. */
export function idDoVinculo(instituicaoId: string, uid: string): string {
  return `${instituicaoId}_${uid}`;
}

/**
 * As transições que um vínculo aceita.
 *
 * Escrito como tabela em vez de espalhado em `if` pelas telas: assim dá para ler todas de uma vez,
 * e a regra do Firestore afirma a MESMA tabela. Duas afirmações da mesma coisa em dois lugares é
 * o que permite a prova de regras verificar uma contra a outra.
 *
 * `recusado -> pendente` existe porque a pessoa pode ter errado a instituição, ou mudado de
 * escola. `T2.70` levanta justamente o caso de borda: *"pode acontecer de ser um aluno que é da
 * escola e não tá vinculado àquele professor (...) ok, ele pegar e não colocar."*
 */
const TRANSICOES: Record<SituacaoVinculo, SituacaoVinculo[]> = {
  pendente: ["ativo", "recusado"],
  ativo: ["removido"],
  recusado: ["pendente"],
  removido: ["pendente"],
};

export function podeTransitar(de: SituacaoVinculo, para: SituacaoVinculo): boolean {
  return TRANSICOES[de]?.includes(para) ?? false;
}

/**
 * Uma pessoa pode ter vínculo com mais de uma instituição.
 *
 * `T2.76` define educador como *"professores, diretores, funcionários, todo mundo que trabalha com
 * educação no contexto daquela instituição"* — e professor dá aula em duas escolas.
 */
export const PERMITE_MULTIPLAS_INSTITUICOES = true;

/** Rótulo legível, para a tela não inventar o próprio vocabulário. */
export const ROTULO_SITUACAO_VINCULO: Record<SituacaoVinculo, string> = {
  pendente: "aguardando aprovação",
  ativo: "ativo",
  recusado: "recusado",
  removido: "removido",
};

export const ROTULO_SITUACAO_INSTITUICAO: Record<SituacaoInstituicao, string> = {
  solicitada: "aguardando a equipe do HQ-Lab ativar",
  ativa: "ativa",
  recusada: "recusada",
  suspensa: "suspensa",
};

/**
 * Id estável a partir do nome, com sufixo aleatório.
 *
 * Duas escolas podem se chamar "Escola Municipal Central" em cidades diferentes, e um slug puro
 * faria a segunda sobrescrever a primeira em silêncio. O sufixo é o que impede isso.
 */
export function gerarId(nome: string): string {
  const slug = nome
    .toLowerCase()
    .normalize("NFD")
    // Remove os diacríticos que o NFD separou da letra.
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
  return `${slug || "instituicao"}-${Math.random().toString(36).slice(2, 8)}`;
}

/* ─────────────────────── a base oficial (instituicoes-ensino) ─────────────────────── */

/**
 * AS REGRAS DE 06/10/2026, PARA OS QUATRO PRODUTOS — repo mvpconsultorialtda/instituicoes-ensino,
 * regras/INSTITUICOES-E-VINCULOS.md. O ponto T18.9: *"ao invés do cara ter que cadastrar
 * manualmente o nome da instituição, ter uma base que ele possa vincular"*.
 *
 * Toda instituição já existe na base do INEP antes de qualquer pessoa chegar, e ninguém digita
 * nome: o id da instituição É o id da base. A regra do banco trava o mesmo formato.
 */
export const FORMATO_ID_DA_BASE = /^(basica|superior):[0-9]+$/;

export function ehIdDaBase(id: string | undefined): boolean {
  return !!id && FORMATO_ID_DA_BASE.test(id);
}

/** Caso F: quem foi recusado pode pedir de novo na hora, mas só entra na fila 7 dias depois. */
export const DIAS_ATE_VOLTAR_A_FILA = 7;

export const MS_ATE_VOLTAR_A_FILA = DIAS_ATE_VOLTAR_A_FILA * 24 * 60 * 60 * 1000;

/** Desde quando o pedido aparece na fila de moderação. Sem recusa anterior, desde já. */
export function entraNaFilaEm(v: Pick<Vinculo, "visivelEmMs">): Date | null {
  return typeof v.visivelEmMs === "number" ? new Date(v.visivelEmMs) : null;
}

export function visivelNaFila(v: Pick<Vinculo, "situacao" | "visivelEmMs">, agora: Date = new Date()): boolean {
  if (v.situacao !== "pendente") return false;
  const desde = entraNaFilaEm(v);
  return !desde || agora.getTime() >= desde.getTime();
}

/** O aviso ao estudante cujo pedido ficou guardado numa instituição ainda não ativa (08/10). */
export const AVISO_INSTITUICAO_INATIVA =
  "A instituição ainda não está ativa no HQ-Lab. Converse com ela para que um educador se cadastre e possa autorizar você — o seu pedido fica guardado até lá.";

/**
 * Quem pode pedir vínculo, pela situação da instituição — regras de 06/10, revistas em 08/10.
 * Suspensa: ninguém. Educador em não ativa: é o pedido de ativação (caso A, e reivindicar uma
 * recusada). Estudante em não ativa: o pedido fica guardado até ela ativar.
 */
export function podePedirVinculo(
  _papel: PapelInstituicao,
  situacao: SituacaoInstituicao | "inexistente"
): boolean {
  return situacao !== "suspensa";
}
